import React, { useState } from 'react';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { userAppData } from '../MainApp.js';
import {
    addPersonalExternalLink,
    deletePersonalExternalLink,
} from './dashboardLinksApi.js';
import './DashboardLinks.css';


const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };


/** Return a safe personal-link array from an API or permissions payload. */
function normalisePersonalLinks(rawLinks) {
    if (!Array.isArray(rawLinks)) {
        return [];
    }

    return rawLinks
        .filter((link) => link && typeof link === 'object')
        .map((link) => ({
            name: String(link.name || '').trim(),
            url: String(link.url || '').trim(),
        }))
        .filter((link) => link.name && link.url)
        .sort((firstLink, secondLink) => firstLink.name.localeCompare(
            secondLink.name,
            undefined,
            { sensitivity: 'base' }
        ));
}


/** Confirm that a submitted link uses an absolute HTTP or HTTPS URL. */
function isValidExternalUrl(url) {
    try {
        const parsedUrl = new URL(url);
        return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch (error) {
        return false;
    }
}


export default function DashboardLinks() {
    const dashboardLinks = Array.isArray(userAppData['dashboardLinks'])
        ? userAppData['dashboardLinks']
        : [];
    const [personalLinks, setPersonalLinks] = useState(() => (
        normalisePersonalLinks(userAppData['personalExtLinks'])
    ));
    const [manageModalOpen, setManageModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [linkName, setLinkName] = useState('');
    const [linkUrl, setLinkUrl] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [deletingLinkKey, setDeletingLinkKey] = useState('');

    /** Store the latest list in both component state and shared app data. */
    function updatePersonalLinks(rawLinks) {
        const updatedLinks = normalisePersonalLinks(rawLinks);
        userAppData['personalExtLinks'] = updatedLinks;
        setPersonalLinks(updatedLinks);
    }

    /** Open a clean add form from the management modal. */
    function openAddModal() {
        setLinkName('');
        setLinkUrl('');
        setAddModalOpen(true);
    }

    /** Close the add modal unless a request is currently being submitted. */
    function closeAddModal() {
        if (!isAdding) {
            setAddModalOpen(false);
        }
    }

    /** Validate and send a new personal link to the authenticated-user API. */
    async function handleAddLink(event) {
        event.preventDefault();
        const cleanName = linkName.trim();
        const cleanUrl = linkUrl.trim();

        if (!cleanName || cleanName.length > 80) {
            toast.error('❌ Enter a link name of 80 characters or fewer.', TOAST_CONFIG);
            return;
        }
        if (!isValidExternalUrl(cleanUrl)) {
            toast.error('❌ Enter a full URL beginning with http:// or https://.', TOAST_CONFIG);
            return;
        }

        setIsAdding(true);
        try {
            const response = await addPersonalExternalLink({ name: cleanName, url: cleanUrl });
            updatePersonalLinks(response?.personalExtLinks);
            setAddModalOpen(false);
            toast.success('✅ Personal link added.', TOAST_CONFIG);
        } catch (error) {
            console.error('Failed to add personal external link', error);
            toast.error('❌ Personal link could not be added. Check it is not already in your list.', TOAST_CONFIG);
        } finally {
            setIsAdding(false);
        }
    }

    /** Remove one exact personal link after the user selects its bin button. */
    async function handleDeleteLink(link) {
        const linkKey = `${link.name}-${link.url}`;
        setDeletingLinkKey(linkKey);
        try {
            const response = await deletePersonalExternalLink(link);
            updatePersonalLinks(response?.personalExtLinks);
            toast.success('✅ Personal link removed.', TOAST_CONFIG);
        } catch (error) {
            console.error('Failed to delete personal external link', error);
            toast.error('❌ Personal link could not be removed. Please try again.', TOAST_CONFIG);
        } finally {
            setDeletingLinkKey('');
        }
    }

    return (
        <div className='dashboardLinks_main_div'>
            <h1 className='dashboard_tabHeaders'>External Links</h1>

            <div className='dashboardLinks_scrollRegion'>
                <section className='dashboardLinks_section' aria-labelledby='school-links-heading'>
                    <h2 id='school-links-heading' className='dashboardLinks_sectionHeading'>School Links</h2>
                    {userAppData.dashboardLinksError && <p role='status' className='dashboardLinks_emptyState'>Unable to get links</p>}
                    <div className='dashboardLinks_grid'>
                        {dashboardLinks.map((item) => (
                            <a
                                className='dashboardLinks_anchor'
                                key={`${item.text}-${item.link}`}
                                href={item.link}
                                target='_blank'
                                rel='noreferrer'
                            >
                                <div className='dashboardLinks_individual_link'>
                                    <img className='dashboardLinks_img' src={item.image} alt='' />
                                    <p className='dashboardLinks_p'>{item.text}</p>
                                </div>
                            </a>
                        ))}
                    </div>
                </section>

                <section className='dashboardLinks_section' aria-labelledby='personal-links-heading'>
                    <div className='dashboardLinks_personalHeadingRow'>
                        <h2 id='personal-links-heading' className='dashboardLinks_sectionHeading'>Personal Links</h2>
                        <button
                            type='button'
                            className='dashboardLinks_manageButton'
                            onClick={() => setManageModalOpen(true)}
                        >
                            Manage
                        </button>
                    </div>

                    {personalLinks.length > 0 ? (
                        <div className='dashboardLinks_grid dashboardLinks_personalGrid'>
                            {personalLinks.map((link) => (
                                <a
                                    className='dashboardLinks_anchor'
                                    key={`${link.name}-${link.url}`}
                                    href={link.url}
                                    target='_blank'
                                    rel='noreferrer'
                                >
                                    <div className='dashboardLinks_individual_link dashboardLinks_personalCard'>
                                        <p className='dashboardLinks_p'>{link.name}</p>
                                    </div>
                                </a>
                            ))}
                        </div>
                    ) : (
                        <p className='dashboardLinks_emptyState'>Use Manage to add links you open regularly.</p>
                    )}
                </section>
            </div>

            {manageModalOpen && (
                <div className='dashboardLinks_modalOverlay' role='presentation' onMouseDown={() => setManageModalOpen(false)}>
                    <div
                        className='dashboardLinks_modal'
                        role='dialog'
                        aria-modal='true'
                        aria-labelledby='manage-personal-links-title'
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <button
                            type='button'
                            className='dashboardLinks_modalClose'
                            aria-label='Close personal links manager'
                            onClick={() => setManageModalOpen(false)}
                        >
                            <IoClose />
                        </button>

                        <div className='dashboardLinks_modalTitleRow'>
                            <div>
                                <p className='dashboardLinks_modalKicker'>Dashboard</p>
                                <h2 id='manage-personal-links-title'>Manage Personal Links</h2>
                            </div>
                            <button type='button' className='dashboardLinks_addButton' onClick={openAddModal}>
                                <FaPlus aria-hidden='true' />
                                Add link
                            </button>
                        </div>

                        <div className='dashboardLinks_manageList'>
                            {personalLinks.length > 0 ? personalLinks.map((link) => {
                                const linkKey = `${link.name}-${link.url}`;
                                const isDeleting = deletingLinkKey === linkKey;
                                return (
                                    <div className='dashboardLinks_manageRow' key={linkKey}>
                                        <div className='dashboardLinks_manageText'>
                                            <strong>{link.name}</strong>
                                            <span>{link.url}</span>
                                        </div>
                                        <button
                                            type='button'
                                            className='dashboardLinks_deleteButton'
                                            aria-label={`Delete ${link.name}`}
                                            disabled={Boolean(deletingLinkKey)}
                                            onClick={() => handleDeleteLink(link)}
                                        >
                                            <FaTrashAlt aria-hidden='true' />
                                            <span>{isDeleting ? 'Removing…' : 'Remove'}</span>
                                        </button>
                                    </div>
                                );
                            }) : (
                                <p className='dashboardLinks_manageEmpty'>You have not added any personal links yet.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {addModalOpen && (
                <div className='dashboardLinks_modalOverlay dashboardLinks_modalOverlay--add' role='presentation' onMouseDown={closeAddModal}>
                    <div
                        className='dashboardLinks_modal dashboardLinks_addModal'
                        role='dialog'
                        aria-modal='true'
                        aria-labelledby='add-personal-link-title'
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <button
                            type='button'
                            className='dashboardLinks_modalClose'
                            aria-label='Close add link form'
                            disabled={isAdding}
                            onClick={closeAddModal}
                        >
                            <IoClose />
                        </button>

                        <p className='dashboardLinks_modalKicker'>Personal Links</p>
                        <h2 id='add-personal-link-title'>Add a new link</h2>

                        <form className='dashboardLinks_addForm' onSubmit={handleAddLink}>
                            <label htmlFor='personal-link-name'>Link name</label>
                            <input
                                id='personal-link-name'
                                type='text'
                                maxLength='80'
                                value={linkName}
                                disabled={isAdding}
                                onChange={(event) => setLinkName(event.target.value)}
                                placeholder='For example, Staff Portal'
                                autoFocus
                                required
                            />

                            <label htmlFor='personal-link-url'>URL</label>
                            <input
                                id='personal-link-url'
                                type='url'
                                maxLength='2048'
                                value={linkUrl}
                                disabled={isAdding}
                                onChange={(event) => setLinkUrl(event.target.value)}
                                placeholder='https://example.com'
                                required
                            />

                            <button type='submit' className='dashboardLinks_submitButton' disabled={isAdding}>
                                {isAdding ? 'Adding…' : 'Add personal link'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
